import React from "react";
import { useTranslation } from "react-i18next";
import { TrashIcon } from "@aureskonnect/react-ui";
import { BiImageAdd } from "react-icons/bi";
import ReactSwitch from "react-switch";
import { SketchPicker, ColorResult } from "react-color";
import swal from "@sweetalert/with-react";
import DOMPurify from "dompurify";

import { useDialogA11y } from "@pages/Applications/ScanToStart/ScanTo/ModalContentWrapper/shared/useDialogA11y";
import { SafeSvgDataUrl } from "@pages/GeneralConfig/Design/Preview/OrderTaking/GenericInterfacePreview/displayStyleHelpers";

import {
    CATEGORY_BORDER_STYLE_NONE,
    CATEGORY_BORDER_STYLE_SVG,
    CATEGORY_BORDER_STYLE_UNI,
    CATEGORY_OPACITY_MAX,
    CATEGORY_OPACITY_MIN,
    CATEGORY_SVG_MIME_TYPE,
    CATEGORY_SVG_FILE_EXTENSION,
    DEFAULT_CATEGORY_BORDER_COLOR,
    MAX_SVG_SIZE_BYTES,
} from "@constants/index";

// Durée (ms) d'affichage du toast swal d'erreur d'upload.
const SVG_ERROR_TOAST_MS = 2000;

// Détection de la racine <svg> (texte XML). Partagée par la validation du
// contenu décodé et du contenu purgé.
const SVG_ROOT_REGEX = /<svg[\s>]/i;

// Octets de BOM UTF-16 (premiers octets du fichier). Servent à détecter
// l'encodage avant décodage.
const UTF16_BOM_BYTE_FE = 0xfe;
const UTF16_BOM_BYTE_FF = 0xff;

// Décode le contenu binaire d'un SVG (issu de `atob`) en texte propre, quel que
// soit son encodage : UTF-8 (défaut) ou UTF-16 LE/BE (détecté via BOM). Sans
// ça, un SVG enregistré en UTF-16 — chaque caractère intercalé d'un octet nul
// (`<\0s\0v\0g`) — ferait échouer la détection `<svg>` ALORS QUE le fichier est
// parfaitement valide (cas fréquent des exports Windows / Notepad « Unicode »).
function decodeSvgBinaryToText(binary: string): string {
    const firstByte = binary.charCodeAt(0);
    const secondByte = binary.charCodeAt(1);
    const isUtf16LE =
        firstByte === UTF16_BOM_BYTE_FF && secondByte === UTF16_BOM_BYTE_FE;
    const isUtf16BE =
        firstByte === UTF16_BOM_BYTE_FE && secondByte === UTF16_BOM_BYTE_FF;
    if (isUtf16LE || isUtf16BE) {
        // On saute le BOM (2 octets) et on recompose chaque code unit à partir
        // de sa paire d'octets selon l'endianness.
        let text = "";
        for (let i = 2; i + 1 < binary.length; i += 2) {
            const lowOrFirst = binary.charCodeAt(i);
            const highOrSecond = binary.charCodeAt(i + 1);
            text += String.fromCharCode(
                isUtf16LE
                    ? lowOrFirst | (highOrSecond << 8)
                    : (lowOrFirst << 8) | highOrSecond
            );
        }
        return text;
    }
    // UTF-8 : on reconstruit l'unicode depuis la chaîne binaire Latin-1. On
    // percent-encode chaque octet puis on laisse decodeURIComponent l'interpréter
    // en UTF-8 — sans `escape` (déprécié, ES Annexe B). En cas d'échec (octets
    // invalides), on garde le brut.
    try {
        return decodeURIComponent(
            Array.from(
                binary,
                (char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`
            ).join("")
        );
    } catch {
        return binary;
    }
}

// z-index du popover de couleur : au-dessus du contenu du panneau et de la
// preview. Le popover est en position fixed pour sortir du flux scrollable.
const COLOR_POPOVER_Z_INDEX = 1100;
// Décalage horizontal (px) entre la pastille et le popover (ouvert à droite).
const COLOR_POPOVER_OFFSET = 8;
// Dimensions approximatives du SketchPicker, pour garder le popover dans le
// viewport (clamp). Marge de sécurité vis-à-vis des bords.
const COLOR_POPOVER_WIDTH = 230;
const COLOR_POPOVER_HEIGHT = 320;
const COLOR_POPOVER_MARGIN = 8;

// Cadre carré bordé pour les boutons d'action du cadre SVG (corbeille +
// upload), aligné sur la maquette.
const ICON_BUTTON_STYLE = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "38px",
    height: "38px",
    border: "1px solid #CECECE",
    borderRadius: "4px",
    background: "#FFFFFF",
    cursor: "pointer",
    padding: 0,
    marginBottom: 0,
} as React.CSSProperties;

// Style commun des boutons radio "Style de bordure" : rond aligné, taille
// fixe et couleur de la marque.
const BORDER_STYLE_RADIO_STYLE = {
    marginRight: "8px",
    width: "16px",
    height: "16px",
    accentColor: DEFAULT_CATEGORY_BORDER_COLOR,
    cursor: "pointer",
    flexShrink: 0,
} as React.CSSProperties;

type ColorSwatchPropsType = {
    color: string;
    onChange: (hex: string) => void;
    testId: string;
    // Nom accessible du bouton pastille. DisplayStyleSettings monte jusqu'à 3
    // ColorSwatch dans le même panneau (fond / texte / bordure) : un label
    // contextuel par pastille est requis pour des noms accessibles uniques
    // (WCAG 4.1.2). À défaut, on retombe sur le générique "Color picker".
    ariaLabel?: string;
};

type ColorPickerDialogPropsType = {
    color: string;
    onChange: (hex: string) => void;
    onClose: () => void;
    position: { top: number; left: number };
    testId: string;
};

// Dialog du SketchPicker, monté UNIQUEMENT quand le picker est ouvert. En
// montant useDialogA11y ICI (et non dans ColorSwatch toujours monté), les
// comportements WCAG s'alignent sur le cycle de vie réel du dialog :
//   - focus trap Tab/Shift+Tab actif tant que le dialog est ouvert ;
//   - focus initial posé dans le dialog à l'ouverture ;
//   - focus restauré à la pastille déclencheuse à la fermeture (démontage).
// Échap (géré par useDialogA11y) n'est donc plus un listener global partagé par
// toutes les pastilles : un seul dialog est monté à la fois.
function ColorPickerDialog({
    color,
    onChange,
    onClose,
    position,
    testId,
}: ColorPickerDialogPropsType): JSX.Element {
    const { t } = useTranslation();
    const { dialogRef } = useDialogA11y({ onCancel: onClose });

    return (
        <React.Fragment>
            {/* Overlay plein écran : ferme au clic extérieur. Purement décoratif
                côté clavier/AT : role="presentation" + aria-hidden pour que les
                lecteurs d'écran l'ignorent. La fermeture clavier intentionnelle
                passe par Échap (useDialogA11y), pas par cet overlay non
                focusable. */}
            <div
                role="presentation"
                aria-hidden="true"
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: COLOR_POPOVER_Z_INDEX,
                }}
                onClick={onClose}
            />
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label={t("Color picker")}
                data-testid={`${testId}-popover`}
                style={{
                    position: "fixed",
                    top: position.top,
                    left: position.left,
                    zIndex: COLOR_POPOVER_Z_INDEX + 1,
                }}
            >
                <SketchPicker
                    color={color}
                    onChange={(c: ColorResult) => onChange(c.hex)}
                />
            </div>
        </React.Fragment>
    );
}

function ColorSwatch({
    color,
    onChange,
    testId,
    ariaLabel,
}: ColorSwatchPropsType): JSX.Element {
    const { t } = useTranslation();
    const [isPickerOpen, setIsPickerOpen] = React.useState(false);
    // Position fixe (viewport) du popover, calculée sous la pastille à
    // l'ouverture. `fixed` sort du flux scrollable : aucun scroll n'apparaît.
    const [pickerPosition, setPickerPosition] = React.useState({
        top: 0,
        left: 0,
    });
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    // Ferme le popover et rend explicitement le focus à la pastille. Le dialog
    // (via useDialogA11y) restaure aussi le focus à son démontage, mais on le
    // fait ici de façon déterministe : selon le déclencheur de fermeture (clic
    // overlay, Échap) l'élément actif capturé au montage peut être le <body>
    // (notamment hors navigateur réel), ce qui ne ramènerait pas à la pastille.
    function closePicker(): void {
        setIsPickerOpen(false);
        buttonRef.current?.focus();
    }

    function openPicker(): void {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) {
            // Ouvre à droite de la pastille ; bascule à gauche si ça déborde.
            let left = rect.right + COLOR_POPOVER_OFFSET;
            if (left + COLOR_POPOVER_WIDTH > window.innerWidth) {
                left = rect.left - COLOR_POPOVER_WIDTH - COLOR_POPOVER_OFFSET;
            }
            // Aligne verticalement sur la pastille, en gardant le popover dans
            // le viewport (ne déborde ni en bas ni en haut).
            const maxTop =
                window.innerHeight -
                COLOR_POPOVER_HEIGHT -
                COLOR_POPOVER_MARGIN;
            const top = Math.max(
                COLOR_POPOVER_MARGIN,
                Math.min(rect.top, maxTop)
            );
            setPickerPosition({ top, left });
        }
        setIsPickerOpen(true);
    }

    return (
        <div
            style={{
                position: "relative",
                display: "inline-flex",
                verticalAlign: "middle",
            }}
        >
            <button
                ref={buttonRef}
                type="button"
                data-testid={testId}
                onClick={() => (isPickerOpen ? closePicker() : openPicker())}
                style={{
                    backgroundColor: color,
                    width: "28px",
                    height: "28px",
                    borderRadius: "4px",
                    border: "1px solid #CECECE",
                    cursor: "pointer",
                    padding: 0,
                    display: "block",
                }}
                aria-label={ariaLabel ?? t("Color picker")}
                aria-haspopup="dialog"
                aria-expanded={isPickerOpen}
            />
            {isPickerOpen ? (
                <ColorPickerDialog
                    color={color}
                    onChange={onChange}
                    onClose={closePicker}
                    position={pickerPosition}
                    testId={testId}
                />
            ) : null}
        </div>
    );
}

export type DisplayStyleValues = {
    backgroundColor: string;
    backgroundOpacity: number;
    selectedTextColor: string;
    borderColor: string;
    borderStyle: string;
    /**
     * Data URL base64 `data:image/svg+xml;base64,…` du cadre SVG, purgée par
     * DOMPurify à l'upload. SÉCURITÉ : à utiliser UNIQUEMENT comme `src` d'image
     * ou `url()` CSS (border-image), via `safeGetSvgBorderCssUrl`. NE JAMAIS
     * insérer via `innerHTML` / `dangerouslySetInnerHTML` (re-purge obligatoire
     * sinon — le contenu décodé pourrait réintroduire un vecteur XSS).
     */
    svgBorder: string;
    svgBorderName: string;
};

export type DisplayStyleSetters = {
    setBackgroundColor: (color: string) => void;
    setBackgroundOpacity: (opacity: number) => void;
    setSelectedTextColor: (color: string) => void;
    setBorderColor: (color: string) => void;
    setBorderStyle: (style: string) => void;
    /**
     * Persiste la data URL base64 du cadre SVG (déjà purgée DOMPurify). Voir la
     * note SÉCURITÉ sur `DisplayStyleValues.svgBorder` : usage `src`/`url()`
     * seulement, jamais `innerHTML`.
     */
    setSvgBorder: (svg: string) => void;
    setSvgBorderName: (name: string) => void;
};

type DisplayStyleSettingsPropsType = {
    values: DisplayStyleValues;
    setters: DisplayStyleSetters;
    // Persiste les changements dans le template (la clé exacte dépend du
    // contexte : catégorie ou produit). On reçoit un patch partiel dont les
    // clés sont les noms réels des champs côté template.
    persist: (extra: Record<string, unknown>) => void;
    // Résout les sentinelles de couleur (__primary__/__secondary__) en hex.
    resolveColor: (color: string) => string;
    // Préfixe des data-testid : ex "order-taking-menu-mode-2" ou
    // "order-taking-product".
    testIdPrefix: string;
    // Libellé de la ligne "couleur de fond" (diffère cat. vs produit).
    backgroundColorLabel: string;
    // Libellé de la ligne "couleur du texte".
    textColorLabel: string;
    // Libellés du bloc cadre SVG (titre + sous-titre), variables selon le
    // contexte (catégorie / produit).
    svgFrameTitle: string;
    svgFrameSubtitle: string;
    // Met le titre du cadre SVG en gras (défaut : true, comportement historique
    // des panneaux catégorie / produit). L'étape produit composé le passe à
    // false pour un titre non gras.
    svgFrameTitleBold?: boolean;
    // Clés de persistance (noms réels des champs côté template).
    persistKeys: Record<keyof DisplayStyleValues, string>;
    // Sélecteur "Mode d'affichage" optionnel (Mode 1 / Mode 2).
    modeSelector?: {
        label: string;
        mode1Label: string;
        mode2Label: string;
        mode1Value: string;
        mode2Value: string;
        currentMode: string;
        onChange: (mode: string) => void;
        testIdPrefix: string;
    };
};

export function DisplayStyleSettings({
    values,
    setters,
    persist,
    resolveColor,
    testIdPrefix,
    backgroundColorLabel,
    textColorLabel,
    svgFrameTitle,
    svgFrameSubtitle,
    svgFrameTitleBold = true,
    persistKeys,
    modeSelector,
}: DisplayStyleSettingsPropsType): JSX.Element {
    const { t } = useTranslation();
    // Ref vers l'input[type=file] caché : le déclencheur visible est un
    // <button> (focusable clavier dans tous les navigateurs) qui relaie le clic.
    const svgInputRef = React.useRef<HTMLInputElement>(null);

    function showSvgError(message: string): void {
        swal({
            icon: "error",
            content: <p>{message}</p>,
            buttons: false,
            timer: SVG_ERROR_TOAST_MS,
        });
    }

    function handleBackgroundColorChange(hex: string): void {
        setters.setBackgroundColor(hex);
        persist({ [persistKeys.backgroundColor]: hex });
    }

    function handleBackgroundToggle(isActive: boolean): void {
        // ON = fond opaque, OFF = fond transparent.
        const value = isActive ? CATEGORY_OPACITY_MAX : CATEGORY_OPACITY_MIN;
        setters.setBackgroundOpacity(value);
        persist({ [persistKeys.backgroundOpacity]: value });
    }

    function handleSelectedTextColorChange(hex: string): void {
        setters.setSelectedTextColor(hex);
        persist({ [persistKeys.selectedTextColor]: hex });
    }

    function handleBorderStyleChange(style: string): void {
        setters.setBorderStyle(style);
        persist({ [persistKeys.borderStyle]: style });
    }

    function handleBorderColorChange(hex: string): void {
        setters.setBorderColor(hex);
        persist({ [persistKeys.borderColor]: hex });
    }

    function handleBorderSvgUpload(
        event: React.ChangeEvent<HTMLInputElement>
    ): void {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        const input = event.target;
        // SÉCURITÉ — défense en profondeur (best-effort côté client) :
        //   1) Garde rapide MIME + extension ci-dessous : `file.type` et le nom
        //      sont fournis par le client donc CONTOURNABLES (un fichier renommé
        //      .svg avec un MIME usurpé passe cette étape) — ce n'est qu'un
        //      premier filtre, pas une garantie.
        //   2) Vraie validation : présence d'une racine <svg> dans le contenu
        //      décodé (SVG_ROOT_REGEX, non ancrée → tolère un prologue XML/DOCTYPE).
        //   3) Purge XSS DOMPurify (profil svg) puis re-vérification <svg>.
        // ⚠️ Le BACKEND ne doit JAMAIS faire confiance à cette validation : il
        //    doit RE-VALIDER et RE-PURGER tout SVG reçu avant stockage/diffusion
        //    aux kiosks (le store front est manipulable). Cf. OWASP A03:2021.
        const isSvg =
            file.type === CATEGORY_SVG_MIME_TYPE ||
            file.name.toLowerCase().endsWith(CATEGORY_SVG_FILE_EXTENSION);
        if (!isSvg) {
            input.value = "";
            showSvgError(t("File type is not valid"));
            return;
        }
        // Borne la taille AVANT lecture : un SVG est stocké en base64 dans le
        // template (~+33 %), donc un fichier volumineux saturerait le payload
        // API / le stockage et bloquerait silencieusement la sauvegarde.
        // Message dédié : un SVG valide mais trop lourd ne doit PAS être
        // confondu avec un type de fichier invalide.
        if (file.size > MAX_SVG_SIZE_BYTES) {
            input.value = "";
            showSvgError(t("File size is too large. Maximum 300KB."));
            return;
        }
        // Borne la taille AVANT lecture : un SVG est stocké en base64 dans le
        // template (~+33 %), donc un fichier volumineux saturerait le payload
        // API / le stockage et bloquerait silencieusement la sauvegarde.
        // Message dédié : un SVG valide mais trop lourd ne doit PAS être
        // confondu avec un type de fichier invalide.
        if (file.size > MAX_SVG_SIZE_BYTES) {
            input.value = "";
            showSvgError(t("File size is too large. Maximum 300KB."));
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const result =
                typeof reader.result === "string" ? reader.result : "";
            // Validation du contenu réel (équivalent magic-bytes pour le SVG,
            // qui est du XML texte) : on décode la data URL base64 et la racine
            // doit contenir un <svg>. Empêche un fichier renommé .svg.
            const base64 = result.split(",")[1] ?? "";
            let binary = "";
            try {
                binary = atob(base64);
            } catch {
                binary = "";
            }
            // Décodage encoding-agnostique (UTF-8 / UTF-16) avant la détection
            // `<svg>` : un SVG UTF-16 valide ne doit pas être rejeté.
            const decoded = decodeSvgBinaryToText(binary);
            if (!SVG_ROOT_REGEX.test(decoded)) {
                input.value = "";
                showSvgError(t("File type is not valid"));
                return;
            }
            // Purge XSS : un SVG est du XML actif (script inline, onload=,
            // href="javascript:"). On nettoie le contenu décodé avec DOMPurify
            // (profil SVG) AVANT de le réencoder et de le persister. Le champ
            // svgBorder ne doit jamais être réinjecté via innerHTML / dangerouslySetInnerHTML sans re-purge.
            const sanitized = DOMPurify.sanitize(decoded, {
                USE_PROFILES: { svg: true, svgFilters: true },
            });
            if (!SVG_ROOT_REGEX.test(sanitized)) {
                input.value = "";
                showSvgError(t("File type is not valid"));
                return;
            }
            // Réencodage UTF-8-safe : un SVG purgé peut contenir des caractères
            // hors Latin-1 (texte accentué) que btoa() rejetterait. On les
            // percent-encode (encodeURIComponent → octets UTF-8 en %XX) puis on
            // remplace chaque %XX par son octet pour obtenir une chaîne binaire
            // Latin-1 acceptée par btoa. On évite `unescape` (déprécié, ES Annexe
            // B) et `TextEncoder` (absent de l'environnement jsdom des tests). Si
            // l'encodage échoue, on refuse le fichier.
            // sanitizedDataUrl porte le type de marque SafeSvgDataUrl : c'est
            // le SEUL point où on minte ce type (après purge DOMPurify +
            // réencodage). Toute valeur typée SafeSvgDataUrl est donc passée par
            // cette frontière de confiance. NE JAMAIS l'injecter via innerHTML.
            let sanitizedDataUrl: SafeSvgDataUrl = "" as SafeSvgDataUrl;
            try {
                const binary = encodeURIComponent(
                    sanitized
                ).replace(/%([0-9A-F]{2})/g, (_match, hex) =>
                    String.fromCharCode(parseInt(hex, 16))
                );
                sanitizedDataUrl = `data:${CATEGORY_SVG_MIME_TYPE};base64,${btoa(
                    binary
                )}` as SafeSvgDataUrl;
            } catch {
                input.value = "";
                showSvgError(t("File type is not valid"));
                return;
            }
            setters.setSvgBorder(sanitizedDataUrl);
            setters.setSvgBorderName(file.name);
            persist({
                [persistKeys.svgBorder]: sanitizedDataUrl,
                [persistKeys.svgBorderName]: file.name,
            });
            // Reset only after the async read completes, so re-selecting the
            // same file fires onChange again without aborting the read.
            input.value = "";
        };
        reader.onerror = () => {
            reader.abort();
            input.value = "";
            showSvgError(t("File type is not valid"));
        };
        reader.readAsDataURL(file);
    }

    function handleBorderSvgDelete(): void {
        setters.setSvgBorder("");
        setters.setSvgBorderName("");
        persist({
            [persistKeys.svgBorder]: "",
            [persistKeys.svgBorderName]: "",
        });
    }

    const isBorderColorVisible =
        values.borderStyle === CATEGORY_BORDER_STYLE_UNI;
    const isSvgBorder = values.borderStyle === CATEGORY_BORDER_STYLE_SVG;

    return (
        <div data-testid={`${testIdPrefix}-settings`}>
            {modeSelector ? (
                <div className="py-2 d-flex align-items-start">
                    <div
                        style={{ flex: 1 }}
                        id={`${modeSelector.testIdPrefix}-group-label`}
                    >
                        {modeSelector.label}
                    </div>
                    <div
                        className="d-flex flex-column align-items-start"
                        style={{ flex: 1, gap: "6px" }}
                        role="radiogroup"
                        aria-labelledby={`${modeSelector.testIdPrefix}-group-label`}
                    >
                        <label
                            className="d-flex align-items-center mb-0"
                            style={{ cursor: "pointer" }}
                        >
                            <input
                                type="radio"
                                name={`${modeSelector.testIdPrefix}-mode`}
                                data-testid={`${modeSelector.testIdPrefix}-mode-1-radio`}
                                checked={
                                    modeSelector.currentMode ===
                                    modeSelector.mode1Value
                                }
                                onChange={() =>
                                    modeSelector.onChange(
                                        modeSelector.mode1Value
                                    )
                                }
                                style={BORDER_STYLE_RADIO_STYLE}
                            />
                            {modeSelector.mode1Label}
                        </label>
                        <label
                            className="d-flex align-items-center mb-0"
                            style={{ cursor: "pointer" }}
                        >
                            <input
                                type="radio"
                                name={`${modeSelector.testIdPrefix}-mode`}
                                data-testid={`${modeSelector.testIdPrefix}-mode-2-radio`}
                                checked={
                                    modeSelector.currentMode ===
                                    modeSelector.mode2Value
                                }
                                onChange={() =>
                                    modeSelector.onChange(
                                        modeSelector.mode2Value
                                    )
                                }
                                style={BORDER_STYLE_RADIO_STYLE}
                            />
                            {modeSelector.mode2Label}
                        </label>
                    </div>
                </div>
            ) : null}

            <div className="py-2 d-flex align-items-center">
                <div style={{ flex: 1 }}>{backgroundColorLabel}</div>
                <div style={{ flex: 1 }}>
                    <ColorSwatch
                        color={resolveColor(values.backgroundColor)}
                        onChange={handleBackgroundColorChange}
                        testId={`${testIdPrefix}-bg-color-swatch`}
                        ariaLabel={`${backgroundColorLabel} ${t(
                            "Color picker"
                        )}`}
                    />
                </div>
            </div>

            <div className="py-2 d-flex align-items-center">
                <div style={{ flex: 1 }}>{t("Background opacity")}</div>
                <div style={{ flex: 1 }}>
                    <ReactSwitch
                        uncheckedIcon={false}
                        checkedIcon={false}
                        handleDiameter={26}
                        offColor="#f7b4b8"
                        offHandleColor="#E30613"
                        checked={
                            values.backgroundOpacity > CATEGORY_OPACITY_MIN
                        }
                        onChange={handleBackgroundToggle}
                        onColor="#c2eddd"
                        onHandleColor="#34C38F"
                        width={50}
                        height={20}
                        aria-label={t("Background opacity")}
                        data-testid={`${testIdPrefix}-opacity-switch`}
                    />
                </div>
            </div>

            <div className="py-2 d-flex align-items-center">
                <div style={{ flex: 1 }}>{textColorLabel}</div>
                <div style={{ flex: 1 }}>
                    <ColorSwatch
                        color={resolveColor(values.selectedTextColor)}
                        onChange={handleSelectedTextColorChange}
                        testId={`${testIdPrefix}-text-color-swatch`}
                        ariaLabel={`${textColorLabel} ${t("Color picker")}`}
                    />
                </div>
            </div>

            <div className="py-2 d-flex align-items-start">
                <div
                    style={{ flex: 1 }}
                    id={`${testIdPrefix}-border-style-group-label`}
                >
                    {t("Border style")}
                </div>
                <div
                    className="d-flex flex-column align-items-start"
                    style={{ flex: 1, gap: "6px" }}
                    role="radiogroup"
                    aria-labelledby={`${testIdPrefix}-border-style-group-label`}
                >
                    <label
                        className="d-flex align-items-center mb-0"
                        style={{ cursor: "pointer" }}
                    >
                        <input
                            type="radio"
                            name={`${testIdPrefix}-border-style`}
                            data-testid={`${testIdPrefix}-border-style-none-radio`}
                            checked={
                                values.borderStyle ===
                                CATEGORY_BORDER_STYLE_NONE
                            }
                            onChange={() =>
                                handleBorderStyleChange(
                                    CATEGORY_BORDER_STYLE_NONE
                                )
                            }
                            style={BORDER_STYLE_RADIO_STYLE}
                        />
                        {t("none")}
                    </label>
                    <label
                        className="d-flex align-items-center mb-0"
                        style={{ cursor: "pointer" }}
                    >
                        <input
                            type="radio"
                            name={`${testIdPrefix}-border-style`}
                            data-testid={`${testIdPrefix}-border-style-uni-radio`}
                            checked={
                                values.borderStyle === CATEGORY_BORDER_STYLE_UNI
                            }
                            onChange={() =>
                                handleBorderStyleChange(
                                    CATEGORY_BORDER_STYLE_UNI
                                )
                            }
                            style={BORDER_STYLE_RADIO_STYLE}
                        />
                        {t("Solid color")}
                    </label>
                    <label
                        className="d-flex align-items-center mb-0"
                        style={{ cursor: "pointer" }}
                    >
                        <input
                            type="radio"
                            name={`${testIdPrefix}-border-style`}
                            data-testid={`${testIdPrefix}-border-style-svg-radio`}
                            checked={
                                values.borderStyle === CATEGORY_BORDER_STYLE_SVG
                            }
                            onChange={() =>
                                handleBorderStyleChange(
                                    CATEGORY_BORDER_STYLE_SVG
                                )
                            }
                            style={BORDER_STYLE_RADIO_STYLE}
                        />
                        {t("SVG")}
                    </label>
                </div>
            </div>

            {isBorderColorVisible ? (
                <div className="py-2 d-flex align-items-center">
                    <div style={{ flex: 1 }}>{t("Border color")}</div>
                    <div style={{ flex: 1 }}>
                        <ColorSwatch
                            color={resolveColor(values.borderColor)}
                            onChange={handleBorderColorChange}
                            testId={`${testIdPrefix}-border-color-swatch`}
                            ariaLabel={`${t("Border color")} ${t(
                                "Color picker"
                            )}`}
                        />
                    </div>
                </div>
            ) : null}

            {isSvgBorder ? (
                <div className="py-2 d-flex justify-content-between align-items-center">
                    <div>
                        <div
                            style={{
                                fontWeight: svgFrameTitleBold
                                    ? "bold"
                                    : "normal",
                            }}
                        >
                            {svgFrameTitle}
                        </div>
                        <div style={{ color: "#B7B7B7", fontSize: "13px" }}>
                            {svgFrameSubtitle}
                        </div>
                    </div>
                    <div
                        className="d-flex align-items-center"
                        style={{ gap: "8px" }}
                    >
                        <button
                            type="button"
                            disabled={values.svgBorder === ""}
                            onClick={handleBorderSvgDelete}
                            aria-label={t("Delete")}
                            data-testid={`${testIdPrefix}-border-svg-delete-btn`}
                            style={ICON_BUTTON_STYLE}
                        >
                            <TrashIcon
                                style={{ fill: "red" }}
                                height={20}
                                width={20}
                            />
                        </button>
                        <button
                            type="button"
                            className="m-0"
                            style={ICON_BUTTON_STYLE}
                            aria-label={t("Upload SVG")}
                            data-testid={`${testIdPrefix}-border-svg-upload-btn`}
                            onClick={() => svgInputRef.current?.click()}
                        >
                            <BiImageAdd size={22} color="#6c757d" />
                        </button>
                        <input
                            ref={svgInputRef}
                            type="file"
                            accept={CATEGORY_SVG_MIME_TYPE}
                            onChange={handleBorderSvgUpload}
                            data-testid={`${testIdPrefix}-border-svg-input`}
                            style={{ display: "none" }}
                        />
                        {values.svgBorderName !== "" ? (
                            <span
                                style={{ fontSize: "13px" }}
                                data-testid={`${testIdPrefix}-border-svg-filename`}
                            >
                                {values.svgBorderName}
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
